import BigNumber from 'bignumber.js'
import { SerializedFarm, DeserializedPool, SerializedPool } from 'state/types'
import { deserializeToken } from 'state/user/hooks/helpers'
import { BIG_ZERO } from 'utils/bigNumber'

// Helper: Auto vault profit
const getVaultProfit = (userShares: BigNumber, pricePerFullShare: BigNumber, cakeAtLastUserAction: BigNumber) => {
  if (!userShares || !pricePerFullShare) return BIG_ZERO
  const currentBalance = userShares.times(pricePerFullShare).div(1e18)
  return currentBalance.minus(cakeAtLastUserAction)
}

type UserData =
  | DeserializedPool['userData']
  | {
      allowance: number | string
      stakingTokenBalance: number | string
      stakedBalance: number | string
      pendingReward: number | string
      userShares?: number | string
      cakeAtLastUserAction?: number | string
      pricePerFullShare?: number | string
    }

export const transformUserData = (userData: UserData) => {
  return {
    allowance: userData ? new BigNumber(userData.allowance) : BIG_ZERO,
    stakingTokenBalance: userData ? new BigNumber(userData.stakingTokenBalance) : BIG_ZERO,
    stakedBalance: userData ? new BigNumber(userData.stakedBalance) : BIG_ZERO,
    pendingReward: userData ? new BigNumber(userData.pendingReward) : BIG_ZERO,
    // vault-specific
    userShares: userData?.userShares ? new BigNumber(userData.userShares) : BIG_ZERO,
    cakeAtLastUserAction: userData?.cakeAtLastUserAction
      ? new BigNumber(userData.cakeAtLastUserAction)
      : BIG_ZERO,
    pricePerFullShare: userData?.pricePerFullShare ? new BigNumber(userData.pricePerFullShare) : BIG_ZERO,
  }
}

export const transformPool = (pool: SerializedPool): DeserializedPool => {
  const { totalStaked, stakingLimit, userData, stakingToken, earningToken, ...rest } = pool

  const deserializedUser = transformUserData(userData)

  // Override pendingReward for auto vault
  let pendingReward = deserializedUser.pendingReward
  if ((pool as any).isAutoVault) {
    pendingReward = getVaultProfit(
      deserializedUser.userShares,
      deserializedUser.pricePerFullShare,
      deserializedUser.cakeAtLastUserAction,
    )
  }

  return {
    ...rest,
    stakingToken: deserializeToken(stakingToken),
    earningToken: deserializeToken(earningToken),
    userData: {
      ...deserializedUser,
      pendingReward,
    },
    totalStaked: totalStaked ? new BigNumber(totalStaked) : BIG_ZERO,
    stakingLimit: stakingLimit ? new BigNumber(stakingLimit) : BIG_ZERO,
  }
}

export const getTokenPricesFromFarm = (farms: SerializedFarm[]) => {
  return farms.reduce((prices, farm) => {
    const quoteTokenAddress = farm.quoteToken.address.toLocaleLowerCase()
    const tokenAddress = farm.token.address.toLocaleLowerCase()
    if (!prices[quoteTokenAddress]) {
      prices[quoteTokenAddress] = new BigNumber(farm.quoteTokenPriceBusd).toNumber()
    }
    if (!prices[tokenAddress]) {
      prices[tokenAddress] = new BigNumber(farm.tokenPriceBusd).toNumber()
    }
    return prices
  }, {} as Record<string, number>)
}
